import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardAccessNotification } from '@/hooks/useDashboardAccessNotifications';
import { formatDistanceToNow } from 'date-fns';

interface DashboardAccessNotificationModalProps {
  notification: DashboardAccessNotification | null;
  open: boolean;
  onClose: () => void;
}

export const DashboardAccessNotificationModal: React.FC<DashboardAccessNotificationModalProps> = ({
  notification,
  open,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    if (!notification) return;

    const updateTimeLeft = () => {
      const now = new Date();
      const expiresAt = new Date(notification.expires_at);
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('Expired');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [notification]);

  const handleCopy = () => {
    if (!notification) return;
    
    navigator.clipboard.writeText(notification.code);
    setCopied(true);
    toast.success('Code copied to clipboard!');
    
    setTimeout(() => setCopied(false), 2000);
  };

  if (!notification) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Dashboard Access Code</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Dashboard Type Badge */}
          <div className="flex justify-center">
            <Badge variant="secondary" className="text-lg py-2 px-4">
              {notification.dashboard_type === 'consultant' ? 'Consultant Dashboard' : 'SDR Dashboard'}
            </Badge>
          </div>

          {/* Code Display */}
          <div className="bg-muted rounded-lg p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Access Code</p>
            <p className="text-5xl font-mono font-bold tracking-wider mb-4">
              {notification.code}
            </p>
            <Button
              onClick={handleCopy}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy Code
                </>
              )}
            </Button>
          </div>

          {/* Time Left */}
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="text-sm">
              Expires in: <span className="font-semibold text-foreground">{timeLeft}</span>
            </span>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>Instructions:</strong> Enter this code on the Raspberry Pi dashboard to grant access for 7 days.
            </p>
          </div>

          {/* Close Button */}
          <Button onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
